import {API} from '@/classes/api/api.class';
import {FindNotificationRequestDto} from '@/classes/dto/notifier/request/FindNotification.request.dto';
import {LeanDocument, Types} from 'mongoose';
import {NotificationDocument} from '@/documentTypes/notifier/Notification.document';
import {FindNotificationFilter} from '@/classes/dto/notifier/request/filters/FindNotification.filter';
import {
    BulkUpdateNotificationStateRequestDto,
} from '@/classes/dto/notifier/request/BulkUpdateNotificationState.request.dto';
import {backendFeatures, isUnknownRoute, probeFeature, silentRequest} from '@/helpers/featureProbe.helper';

/** `GET notifier/counts` (Notify v2) */
export interface NotificationCounts {
    unread: number;
    byType: Record<string, number>;
    open: number;
    mentions: number;
    trash: number;
}

const isCounts = (data: unknown): data is NotificationCounts =>
    !!data && typeof data === 'object' && typeof (data as NotificationCounts).unread === 'number';

export class NotifierApi extends API {
    static service = 'notifier';

    static async getUnreadCount(): Promise<number> {
        return this.get('unread-count');
    }

    static async getOwnNotifications(data: FindNotificationRequestDto & {
        filter?: FindNotificationFilter & { to: never }
    }): Promise<{ notifications: LeanDocument<NotificationDocument>[], maxPage: number }> {
        return this.get('own', data);
    }

    static async markDelivered(data: BulkUpdateNotificationStateRequestDto): Promise<void> {
        return this.put('mark-delivered', data);
    }

    static async markRead(data: BulkUpdateNotificationStateRequestDto): Promise<void> {
        return this.put('mark-read', data);
    }

    static async dismiss(data: BulkUpdateNotificationStateRequestDto): Promise<void> {
        return this.put(`dismiss`, data);
    }

    static async revokeDismission(id: Types.ObjectId): Promise<void> {
        return this.put(`revoke-dismission/${id}`, {});
    }

    /* ---------------- Notify v2 (silent: an old notifier never opens an error dialog) ---------------- */

    /** Server counts per segment; null when the notifier does not offer them (old backend) */
    static async getCounts(): Promise<NotificationCounts | null> {
        if (backendFeatures.notifierV2 === false) {
            return null;
        }
        const result = await silentRequest<NotificationCounts>('get', this.service, 'counts');
        if (result.ok && isCounts(result.data)) {
            backendFeatures.notifierV2 = true;
            return result.data;
        }
        if (backendFeatures.notifierV2 === undefined && (isUnknownRoute(result) || result.ok || result.status === 400)) {
            backendFeatures.notifierV2 = false;
        }
        return null;
    }

    /** true when the notifier supports counts, mark-unread, bulk restore, snooze and dismiss-group */
    static supportsV2(): Promise<boolean> {
        return probeFeature('notifierV2', async () => !!(await this.getCounts()));
    }

    private static async silentPut(path: string, body: unknown): Promise<boolean> {
        if (!(await this.supportsV2())) {
            return false;
        }
        const result = await silentRequest('put', this.service, path, body);
        if (!result.ok && isUnknownRoute(result)) {
            backendFeatures.notifierV2 = false;
        }
        return result.ok;
    }

    /** false: not supported or failed */
    static markUnread(ids: Types.ObjectId[]): Promise<boolean> {
        return ids.length ? this.silentPut('mark-unread', {ids}) : Promise.resolve(true);
    }

    /** false: not supported or failed */
    static snooze(ids: Types.ObjectId[], until: Date): Promise<boolean> {
        return ids.length ? this.silentPut('snooze', {ids, until}) : Promise.resolve(true);
    }

    /** Bulk restore from the trash; falls back to one call per notification on an old notifier */
    static async revokeDismissions(ids: Types.ObjectId[]): Promise<void> {
        if (!ids.length) {
            return;
        }
        if (await this.silentPut('revoke-dismissions', {ids})) {
            return;
        }
        for (const id of ids) {
            await this.revokeDismission(id);
        }
    }

    /**
     * Dismisses a whole group ("Alle 14 entfernen"). New notifier: by groupKey, plus the loaded notifications that
     * were created before groupKey existed (`legacyIds`). Old notifier: all loaded ids of the group.
     */
    static async dismissGroup(groupKey: string, loadedIds: Types.ObjectId[], legacyIds: Types.ObjectId[]): Promise<void> {
        if (await this.silentPut('dismiss-group', {groupKey})) {
            if (legacyIds.length) {
                await this.dismiss({notificationIds: legacyIds});
            }
            return;
        }
        if (loadedIds.length) {
            await this.dismiss({notificationIds: loadedIds});
        }
    }

    /** Catch-up after a reconnect; null when the notifier does not know the filter (old backend) */
    static async getOwnCreatedAfter(createdAfter: Date, limit = 50): Promise<LeanDocument<NotificationDocument>[] | null> {
        if (!(await this.supportsV2())) {
            return null;
        }
        const result = await silentRequest<{ notifications: LeanDocument<NotificationDocument>[] }>('get', this.service, 'own', {
            filter: {createdAfter},
            limit,
            sortBy: 'createdAt',
            sortDesc: true,
            skipPagination: true,
        });
        return result.ok && Array.isArray(result.data?.notifications) ? result.data.notifications : null;
    }
}
